import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Pill, AlertTriangle, Clock, CheckCircle, FileText,
  ChevronRight, Shield, BookOpen, Activity
} from 'lucide-react';
import { getPharmacyDashboard, getCommitteePacks } from '../services/api';

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="card-premium p-5">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-sm text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

function ReviewItem({ item, type }) {
  return (
    <Link 
      to={`/drug/${item.reference_drug_id || item.drug_id}`}
      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${
          type === 'high_risk' ? 'bg-red-500' : 
          type === 'pending' ? 'bg-amber-500' : 'bg-blue-500'
        }`} />
        <div>
          <p className="font-medium text-slate-900">{item.name || item.generic_name}</p>
          <p className="text-sm text-slate-500">{item.drug_name || item.manufacturer}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {item.confidence && (
          <span className="text-sm text-slate-500">{item.confidence}% confidence</span>
        )}
        <ChevronRight className="w-4 h-4 text-slate-400" />
      </div>
    </Link>
  );
}

export default function PharmacyDashboardPage() {
  const [data, setData] = useState(null);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const [dashRes, packsRes] = await Promise.all([
          getPharmacyDashboard(),
          getCommitteePacks(5)
        ]);
        setData(dashRes.data);
        setPacks(packsRes.data.packs || []);
      } catch (error) {
        console.error('Failed to fetch pharmacy dashboard:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="p-6" data-testid="pharmacy-dashboard-loading">
        <div className="skeleton h-8 w-64 mb-6" />
        <div className="grid grid-cols-4 gap-6 mb-6">
          {[1, 2, 3, 4].map(i => <div key={i} className="skeleton h-28 rounded-xl" />)}
        </div>
      </div>
    );
  }

  const evidenceDist = data?.evidence_distribution || {};

  return (
    <div className="p-6" data-testid="pharmacy-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Pharmacy Committee Dashboard</h1>
          <p className="text-slate-500">Clinical governance and substitution review</p>
        </div>
        <Link to="/committee-packs" className="btn-primary flex items-center gap-2">
          <FileText className="w-4 h-4" />
          View Committee Packs
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <StatCard
          icon={Clock}
          label="Under Review"
          value={data?.under_review_count || 0}
          color="bg-blue-500"
        />
        <StatCard
          icon={CheckCircle}
          label="Pending Decisions"
          value={data?.pending_count || 0}
          color="bg-amber-500"
        />
        <StatCard
          icon={AlertTriangle}
          label="High Risk Items"
          value={data?.high_risk_count || 0}
          color="bg-red-500"
        />
        <StatCard
          icon={BookOpen}
          label="High Confidence Evidence"
          value={evidenceDist['High'] || 0}
          color="bg-emerald-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Under Review */}
        <div className="card-premium p-6" data-testid="under-review-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-900">Molecules Under Review</h2>
            <Link to="/pipeline?stage=under_review" className="text-sm text-[#0A2540] hover:underline">
              View all
            </Link>
          </div>
          <div className="space-y-3">
            {(data?.under_review || []).slice(0, 5).map(item => (
              <ReviewItem key={item.id} item={item} type="review" />
            ))}
            {(!data?.under_review || data.under_review.length === 0) && (
              <p className="text-sm text-slate-500 text-center py-4">No items under review</p>
            )}
          </div>
        </div>

        {/* High Risk */}
        <div className="card-premium p-6" data-testid="high-risk-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-500" />
              <h2 className="font-semibold text-slate-900">High Risk Substitutions</h2>
            </div>
          </div>
          <div className="space-y-3">
            {(data?.high_risk_substitutions || []).slice(0, 5).map(item => (
              <ReviewItem key={item.id} item={item} type="high_risk" />
            ))}
            {(!data?.high_risk_substitutions || data.high_risk_substitutions.length === 0) && (
              <div className="text-center py-6">
                <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No high-risk items</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Evidence Distribution */}
      <div className="card-premium p-6 mb-6" data-testid="evidence-distribution">
        <h2 className="font-semibold text-slate-900 mb-4">Evidence Strength Distribution</h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 bg-emerald-50 rounded-xl text-center">
            <p className="text-3xl font-bold text-emerald-600">{evidenceDist['High'] || 0}</p>
            <p className="text-sm text-emerald-700 mt-1">High Confidence</p>
          </div>
          <div className="p-4 bg-amber-50 rounded-xl text-center">
            <p className="text-3xl font-bold text-amber-600">{evidenceDist['Moderate'] || 0}</p>
            <p className="text-sm text-amber-700 mt-1">Moderate Confidence</p>
          </div>
          <div className="p-4 bg-red-50 rounded-xl text-center">
            <p className="text-3xl font-bold text-red-600">{evidenceDist['Low'] || 0}</p>
            <p className="text-sm text-red-700 mt-1">Low Confidence</p>
          </div>
        </div>
      </div>

      {/* Recent Committee Packs */}
      <div className="card-premium p-6" data-testid="recent-packs">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-slate-900">Recent Committee Packs</h2>
          <Link to="/committee-packs" className="text-sm text-[#0A2540] hover:underline">
            View all
          </Link>
        </div>
        <div className="space-y-3">
          {packs.map(pack => (
            <div key={pack.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900">{pack.drug?.brand_name}</p>
                  <p className="text-sm text-slate-500">{pack.generics?.length || 0} alternatives reviewed</p>
                </div>
              </div>
              <span className="text-xs text-slate-400">
                {new Date(pack.generated_at).toLocaleDateString()}
              </span>
            </div>
          ))}
          {packs.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-4">No committee packs generated yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
